/**
 * FicheHeader.tsx
 * ───────────────
 * En-tête d'une fiche d'arrêt : badges, titre, métadonnées.
 */

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { getDateLocale } from '@/i18n';
import type { PublicFiche } from './types';

interface Props {
  fiche:     PublicFiche;
  jur:       string;
  chambre:   string;
  date:      string;
  numero:    string;
  blocked:   boolean;
  remaining: number;
}

// ── Solution (clés i18n fiche.solution.*) ────────────────────────────────────
const SOLUTION_KEYS = new Set([
  'cassation', 'rejet', 'irrecevabilite', 'nonlieu', 'annulation', 'avis',
  'renvoi', 'rabat', 'decheance', 'infirmation', 'confirmation',
  'infirmation_partielle', 'desistement', 'mainlevee', 'other',
]);

const SolutionIcon = ({ solution, size = 32 }: { solution?: string; size?: number }) => {
  const s = (solution || '').toLowerCase().trim();
  const props = {
    width: size, height: size, viewBox: "0 0 48 48",
    fill: "none", xmlns: "http://www.w3.org/2000/svg",
    role: "img" as const, "aria-label": solution,
  };

  if (s === 'cassation' || s === 'infirmation') return (
    <svg {...props}>
      <circle cx="24" cy="24" r="24" fill="#fee2e2"/>
      <line x1="10" y1="38" x2="28" y2="20" stroke="#dc2626" strokeWidth="4" strokeLinecap="round"/>
      <rect x="24" y="10" width="14" height="9" rx="2.5" fill="#dc2626" transform="rotate(-45 31 14)"/>
      <path d="M14 34 l4-4 l-2-3 l4-4" fill="none" stroke="#dc2626" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity=".5"/>
    </svg>
  );
  if (s === 'rejet' || s === 'confirmation') return (
    <svg {...props}>
      <circle cx="24" cy="24" r="24" fill="#dcfce7"/>
      <path d="M24 10 L36 15 L36 26 Q36 35 24 40 Q12 35 12 26 L12 15 Z" fill="#16a34a" fillOpacity=".2" stroke="#16a34a" strokeWidth="2"/>
      <path d="M17 24 l5 5 l9-9" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
  if (s === 'nonlieu' || s === 'non-admission') return (
    <svg {...props}>
      <circle cx="24" cy="24" r="24" fill="#fef9c3"/>
      <rect x="13" y="12" width="22" height="26" rx="2" fill="none" stroke="#ca8a04" strokeWidth="2"/>
      <circle cx="31" cy="25" r="2.5" fill="#ca8a04"/>
      <line x1="13" y1="12" x2="13" y2="38" stroke="#ca8a04" strokeWidth="2.5" strokeLinecap="round"/>
      <line x1="19" y1="18" x2="29" y2="32" stroke="#ca8a04" strokeWidth="1.5" strokeLinecap="round" opacity=".6"/>
      <line x1="29" y1="18" x2="19" y2="32" stroke="#ca8a04" strokeWidth="1.5" strokeLinecap="round" opacity=".6"/>
    </svg>
  );
  if (s === 'irrecevabilite' || s === 'irrecevabilité' || s === 'decheance') return (
    <svg {...props}>
      <circle cx="24" cy="24" r="24" fill="#fef3c7"/>
      <circle cx="24" cy="24" r="13" fill="none" stroke="#d97706" strokeWidth="2.5"/>
      <line x1="14" y1="14" x2="34" y2="34" stroke="#d97706" strokeWidth="2.5" strokeLinecap="round"/>
    </svg>
  );
  if (s === 'renvoi' || s === 'annulation') return (
    <svg {...props}>
      <circle cx="24" cy="24" r="24" fill="#ede9fe"/>
      <path d="M24 12 A12 12 0 1 1 12 24" fill="none" stroke="#7c3aed" strokeWidth="2.5" strokeLinecap="round"/>
      <polygon points="12,17 12,24 19,24" fill="#7c3aed"/>
    </svg>
  );
  if (s.includes('partiel')) return (
    <svg {...props}>
      <circle cx="24" cy="24" r="24" fill="#fef3c7"/>
      <path d="M12 26 l5 5 l9-9" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <line x1="30" y1="16" x2="38" y2="24" stroke="#dc2626" strokeWidth="2" strokeLinecap="round"/>
      <line x1="38" y1="16" x2="30" y2="24" stroke="#dc2626" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );
  if (s === 'desistement') return (
    <svg {...props}>
      <circle cx="24" cy="24" r="24" fill="#e0f2fe"/>
      <path d="M10 26 Q14 20 20 22 L24 24 L28 22 Q34 20 38 26" fill="none" stroke="#0284c7" strokeWidth="2.5" strokeLinecap="round"/>
      <path d="M20 22 L24 26 L28 22" fill="none" stroke="#0284c7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <line x1="24" y1="26" x2="24" y2="34" stroke="#0284c7" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );
  if (s === 'mainlevee' || s === 'mainlevée') return (
    <svg {...props}>
      <circle cx="24" cy="24" r="24" fill="#ede9fe"/>
      <rect x="14" y="12" width="16" height="24" rx="2" fill="none" stroke="#7c3aed" strokeWidth="2"/>
      <path d="M30 18 l6 6 l-6 6" fill="none" stroke="#7c3aed" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
      <line x1="30" y1="24" x2="20" y2="24" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );
  return (
    <svg {...props}>
      <circle cx="24" cy="24" r="24" fill="#f1f5f9"/>
      <text x="24" y="32" textAnchor="middle" fontFamily="system-ui,sans-serif" fontSize="22" fontWeight="700" fill="#94a3b8">?</text>
    </svg>
  );
};

// ── Publication ───────────────────────────────────────────────────────────────
const PUB_CONFIG: Record<string, { labelKey: string; icon: string; color: string }> = {
  'b':  { labelKey: 'fiche.pub.bulletin',   icon: '📋', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  'r':  { labelKey: 'fiche.pub.rapport',    icon: '📖', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
  'l':  { labelKey: 'fiche.pub.lettre',     icon: '✉️', color: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400' },
  'c':  { labelKey: 'fiche.pub.communique', icon: '📢', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
  'p':  { labelKey: 'fiche.pub.bulletin',   icon: '📋', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  'i':  { labelKey: 'fiche.pub.internet',   icon: '🌐', color: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400' },
};

const PubBadges = ({ pub }: { pub?: string[] }) => {
  const { t } = useTranslation();
  if (!pub || pub.length === 0) return (
    <span
      className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full font-medium bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400 ring-1 ring-zinc-300 dark:ring-zinc-600"
      title={t('fiche.pub.unpublished_title')}
    >
      <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
      </svg>
      {t('fiche.pub.unpublished')}
    </span>
  );

  const normalized = pub.map(p => p.toLowerCase());
  const hasB = normalized.includes('b') || normalized.includes('p');
  const hasR = normalized.includes('r');

  if (hasB && hasR) return (
    <span className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 ring-1 ring-emerald-300 dark:ring-emerald-700">
      ⭐ B+R
    </span>
  );

  return (
    <>
      {normalized.filter(p => PUB_CONFIG[p]).map((p, i) => {
        const cfg = PUB_CONFIG[p];
        return (
          <span key={i} className={`inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full font-medium ${cfg.color}`}>
            {cfg.icon} {t(cfg.labelKey)}
          </span>
        );
      })}
    </>
  );
};

// ── Chambre → abréviation standard (format Pappers / Légifrance) ──────────────
const CHAMBRE_ABBREV: [string, string][] = [
  ['criminelle',                    'crim.'],
  ['assemblée plénière',            'ass. plén.'],
  ['chambre mixte',                 'ch. mixte'],
  ['première chambre civile',       '1re civ.'],
  ['deuxième chambre civile',       '2e civ.'],
  ['troisième chambre civile',      '3e civ.'],
  ['chambre civile 1',              '1re civ.'],
  ['chambre civile 2',              '2e civ.'],
  ['chambre civile 3',              '3e civ.'],
  ['1re civ',                       '1re civ.'],
  ['2e civ',                        '2e civ.'],
  ['3e civ',                        '3e civ.'],
  ['chambre sociale',               'soc.'],
  ['sociale',                       'soc.'],
  ['chambre commerciale',           'com.'],
  ['commerciale',                   'com.'],
  ['chambre criminelle',            'crim.'],
];

function abbrevChambre(chambre: string): string {
  const key = chambre.toLowerCase().trim();
  for (const [pattern, abbrev] of CHAMBRE_ABBREV) {
    if (key.includes(pattern)) return abbrev;
  }
  return chambre;
}

// ── Date ISO → date longue (locale courante) ─────────────────────────────────
function formatDateFr(date: string | undefined): string {
  if (!date) return '';
  try {
    return new Date(date).toLocaleDateString(getDateLocale(), {
      day: 'numeric', month: 'long', year: 'numeric',
    });
  } catch {
    return date;
  }
}

// ── Juridiction + ville CA ────────────────────────────────────────────────────
const getCaCity = (location?: string | null, locationLabel?: string | null): string => {
  if (location && location.startsWith('ca_')) {
    const raw = location.slice(3);
    return raw.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join('-');
  }
  if (locationLabel) {
    const m = locationLabel.match(/[Cc]our\s+d[''`\s]appel\s+d[''`\s]?([A-ZÀ-Ÿa-zà-ÿ\s-]+)/u);
    if (m) return m[1].trim().charAt(0).toUpperCase() + m[1].trim().slice(1);
  }
  return '';
};

// ── Signaux « arrêt de principe » → libellé traduit ─────────────────────────
function formatPrincipeSignal(s: string, t: TFunction): string {
  if (s.startsWith('publication:')) {
    const codes = s.replace('publication:', '').toUpperCase();
    if (['B', 'R', 'BR', 'BI', 'BRI'].includes(codes)) return t(`fiche.header.signal.pub_${codes}`);
    return t('fiche.header.signal.publication', { codes });
  }
  if (s === 'regex:revirement')              return t('fiche.header.signal.revirement');
  if (s === 'regex:attendu_principe')        return t('fiche.header.signal.attendu_principe');
  if (s === 'regex:chapeau')                 return t('fiche.header.signal.chapeau');
  if (s === 'regex:visa_principe')           return t('fiche.header.signal.visa_principe');
  if (s === 'regex:formation_haute_texte')   return t('fiche.header.signal.formation_haute');
  if (s.startsWith('regex:attendu_x'))      return t('fiche.header.signal.attendu_repeated', { n: s.split('x')[1] });
  if (s.startsWith('formation:'))            return t('fiche.header.signal.formation', { value: s.replace('formation:', '') });
  if (s.startsWith('citation_count:'))       return t('fiche.header.signal.citing', { value: s.replace('citation_count:', '') });
  if (s.startsWith('importance_score:'))     return t('fiche.header.signal.importance', { value: s.replace('importance_score:', '') });
  if (s.startsWith('bulletin:'))             return t('fiche.header.signal.bulletin', { value: s.replace('bulletin:', '') });
  return s;
}

export function FicheHeader({ fiche }: Props) {
  const { t } = useTranslation();
  const _jur  = (fiche.jurisdiction || '').toLowerCase();
  const isCC  = _jur === 'cc' || _jur.includes('cassation');
  const isCA  = _jur === 'ca' || _jur.includes('appel');
  const caCity = isCA ? getCaCity(fiche.location, fiche.location_label) : '';

  const isArretPrincipe = !!fiche.is_arret_principe;
  const principeScore   = fiche.principe_score ?? null;
  const principeSignals = (fiche.principe_signals ?? []) as string[];
  const visibleSignals  = principeSignals.filter(s => !s.startsWith('llm:'));
  const llmSignal       = principeSignals.find(s => s.startsWith('llm:✅') || s.startsWith('llm:❌'));
  const llmRaison       = llmSignal ? llmSignal.replace(/^llm:[✅❌]\s*/, '') : null;

  const solutionKey       = (fiche.solution || '').toLowerCase();
  const solutionLabel     = fiche.solution
    ? (SOLUTION_KEYS.has(solutionKey) ? t(`fiche.solution.${solutionKey}`) : fiche.solution)
    : null;
  const showSolution      = fiche.solution && fiche.solution !== 'other';
  const showSolutionOther = fiche.solution === 'other' && fiche.solution_raison;

  return (
    <>
      {/* Breadcrumb */}
      <nav className="text-xs text-muted-foreground mb-8 flex items-center gap-1.5">
        <Link to="/"              className="hover:text-foreground transition-colors">{t('fiche.header.home')}</Link>
        <span className="text-muted-foreground/40">/</span>
        <Link to="/jurisprudence" className="hover:text-foreground transition-colors">{t('nav.jurisprudence')}</Link>
        <span className="text-muted-foreground/40">/</span>
        <span className="truncate max-w-xs text-foreground font-mono">{fiche.ecli || fiche.number}</span>
        {document.referrer && document.referrer.includes(window.location.hostname) && (
          <button
            onClick={() => window.history.back()}
            className="ml-auto shrink-0 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            {t('fiche.header.back_to_results')}
          </button>
        )}
      </nav>

      {/* En-tête */}
      <div className="mb-10 pb-10 border-b">

        {/* Ligne badges */}
        <div className="flex flex-wrap gap-2 mb-4 items-center">

          {/* Juridiction */}
          {isCC && (
            <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded font-mono font-bold bg-primary/10 text-primary border border-primary/20">
              Cass.
            </span>
          )}
          {isCA && (
            <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded font-mono font-bold bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400 border border-teal-200 dark:border-teal-700">
              C.A.
            </span>
          )}

          {/* Ville CA */}
          {caCity && (
            <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded bg-teal-50 text-teal-700 dark:bg-teal-900/20 dark:text-teal-300 border border-teal-200 dark:border-teal-800 font-medium">
              <svg width="10" height="13" viewBox="0 0 10 13" fill="none" aria-hidden="true">
                <path d="M5 0C2.24 0 0 2.24 0 5C0 8.75 5 13 5 13C5 13 10 8.75 10 5C10 2.24 7.76 0 5 0ZM5 6.5C4.17 6.5 3.5 5.83 3.5 5C3.5 4.17 4.17 3.5 5 3.5C5.83 3.5 6.5 4.17 6.5 5C6.5 5.83 5.83 6.5 5 6.5Z" fill="currentColor"/>
              </svg>
              {caCity}
            </span>
          )}

          {/* Domaine */}
          {fiche.domaine && (
            <span className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 px-3 py-1 rounded-full font-medium">
              {fiche.domaine}
            </span>
          )}
          {fiche.sous_domaine && fiche.sous_domaine !== fiche.domaine && (
            <span className="text-xs bg-muted text-muted-foreground px-3 py-1 rounded-full">
              {fiche.sous_domaine}
            </span>
          )}

          {/* Solution */}
          {(showSolution || showSolutionOther) && (
            <span className="inline-flex items-center gap-1.5">
              <SolutionIcon solution={fiche.solution} size={28} />
              <span className="text-sm font-semibold text-foreground">
                {showSolution ? solutionLabel : fiche.solution_raison?.slice(0, 50)}
              </span>
            </span>
          )}

          {/* Badge arrêt de principe inline */}
          {isArretPrincipe && (
            <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 border border-amber-300 dark:border-amber-700">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
              </svg>
              {t('fiche.header.principle_badge')}
            </span>
          )}

          {/* Publication */}
          <PubBadges pub={fiche.publication} />

          {/* Citations */}
          {(fiche.citation_count ?? 0) > 0 && (
            <span className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 px-3 py-1 rounded-full font-mono font-bold">
              {t('fiche.header.cited_times', { count: fiche.citation_count })}
            </span>
          )}

          {/* Importance */}
          {(fiche.importance_score ?? 0) > 0 && (
            <span className="text-xs bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400 px-3 py-1 rounded-full font-medium">
              {t('fiche.header.impact', { score: fiche.importance_score })}
            </span>
          )}
        </div>

        {/* Bandeau arrêt de principe */}
        {isArretPrincipe && (
          <div className="flex items-start gap-2.5 px-4 py-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 mb-4">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="#B45309" aria-hidden="true" className="flex-shrink-0 mt-0.5">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
            </svg>
            <div>
              <p className="text-xs font-semibold text-amber-800 dark:text-amber-300">
                {t('fiche.header.principle_title')}{principeScore !== null ? t('fiche.header.principle_index', { score: principeScore }) : ''}
              </p>
              {visibleSignals.length > 0 && (
                <p className="text-[11px] text-amber-700 dark:text-amber-400 mt-0.5">
                  {visibleSignals.map(s => formatPrincipeSignal(s, t)).join(' · ')}
                </p>
              )}
              {llmRaison && (
                <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-1 italic">
                  {llmRaison}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Titre + abréviation */}
        <h1 className="text-2xl sm:text-3xl font-bold leading-tight mb-1 tracking-tight">
          {fiche.location_label || fiche.jurisdiction}
          {fiche.chamber ? ` — ${fiche.chamber}` : ''}
        </h1>
        {/* Référence abrégée standard — ex: Cass. 2e civ., 18 juin 2026, 23-18.170 */}
        {(() => {
          let ref = '';
          if (isCC && fiche.chamber) {
            ref = `Cass. ${abbrevChambre(fiche.chamber)}`;
          } else if (isCA && caCity) {
            ref = `CA ${caCity}`;
          }
          if (!ref) return null;
          if (fiche.decision_date) ref += `, ${formatDateFr(fiche.decision_date)}`;
          if (fiche.number)        ref += `, ${fiche.number}`;
          return (
            <p className="text-xs text-muted-foreground font-mono mb-3 select-all" title={t('fiche.header.short_ref')}>
              {ref}
            </p>
          );
        })()}

        {/* Métadonnées */}
        <p className="text-muted-foreground text-sm flex flex-wrap items-center gap-3">
          <span>{fiche.decision_date}</span>
          <span className="text-muted-foreground/30">·</span>
          <span>{t('fiche.header.number', { number: fiche.number })}</span>
          {fiche.ecli && (
            <>
              <span className="text-muted-foreground/30">·</span>
              <span className="font-mono text-xs bg-muted px-2 py-0.5 rounded">{fiche.ecli}</span>
            </>
          )}
          <span className="text-muted-foreground/30">·</span>
          <span className={`text-xs font-medium ${fiche.quality_score === 100 ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'}`}>
            {t('fiche.header.quality', { score: fiche.quality_score })}
          </span>
        </p>
      </div>
    </>
  );
}